import { createSelector, createFeatureSelector } from '@ngrx/store';
import { State } from './performance.reducer';
import * as moment from 'moment';
import { getYearsDropdownValues, getMonthsDropdownValues } from '../../global/dates';
import { DatesInterval } from '../../global/interfaces';
import {
  Performance,
  PerformanceFilter,
  PerformanceData,
  PerformanceFund,
  PerformancePortfolioTotal,
  PerformanceManager
} from './performance.models';
import * as lodash from 'lodash';
import { isNullOrUndefined } from 'util';

export const selectPerformanceState = createFeatureSelector<State>('performance');

export const selectPerformanceLoading = createSelector(selectPerformanceState, (state: State) => state.loading);

export const selectPerformanceFilter = createSelector(selectPerformanceState, (state: State) => state.filter);

export const selectPerformanceTypeFilter = createSelector(selectPerformanceFilter, (filter: PerformanceFilter) => filter.type);

export const selectPerformanceOptionFilter = createSelector(selectPerformanceFilter, (filter: PerformanceFilter) => filter.option);

export const selectPerformanceYearFilter = createSelector(selectPerformanceFilter, (filter: PerformanceFilter) => filter.year);

export const selectPerformanceMonthFilter = createSelector(selectPerformanceFilter, (filter: PerformanceFilter) => filter.month);

export const selectPerformanceFundFilter = createSelector(selectPerformanceFilter, (filter: PerformanceFilter) => filter.fund);

export const selectPerformance = createSelector(selectPerformanceState, (state: State) => state.data);

export const selectPerformanceData = createSelector(selectPerformance, (state?: Performance) => state ? state.data : undefined);

export const selectPerformanceFunds = createSelector(selectPerformanceData, (state?: PerformanceData) => state ? state.funds : []);

export const selectPerformanceTotal = createSelector(selectPerformanceData, (state?: PerformanceData) => state ? state.total : undefined);

export const selectPerformanceFundsList = createSelector(selectPerformanceFunds, (state: PerformanceFund[]) =>
  !state.length ? [] : lodash.sortBy(state.map((fund: PerformanceFund) =>
    ({ _id: fund._id, name: fund.name })
  ), (fund: PerformanceFund) => fund.name)
);

export const selectPerformanceSelectedFund = createSelector(
  selectPerformanceFundFilter,
  selectPerformanceFunds,
  selectPerformanceTotal,
  (filter?: string, funds?: PerformanceFund[], total?: PerformancePortfolioTotal) => {
    if (!filter || !funds || !funds.length) {
      return undefined;
    }

    if (filter === 'total') {
      return {
        ...total, _id: 'total', name: 'Total Portfolio',
        managers: (isNullOrUndefined(total.fundsDistribution) ?
          [] : total.fundsDistribution) as PerformanceManager[]
      } as PerformanceFund;
    }

    return funds.find(fund => fund._id === filter);
  }
);

export const selectPerformanceDate = createSelector(
  selectPerformanceYearFilter,
  selectPerformanceMonthFilter,
  (year?: number, month?: number) => {
    if (year === undefined || month === undefined) {
      return undefined;
    }
    return moment.utc({ year, month }).startOf('month').format('YYYY-MM-DD');
  }
);

export const selectPerformanceDates = createSelector(selectPerformanceState, (state: State) => state.dates);

export const selectPerformanceDatesMin = createSelector(selectPerformanceDates, (dates?: DatesInterval) =>
  !!dates && dates.min !== undefined && dates.max !== undefined
    ? ((moment(dates.min).format('YYYY-MM-DD').toString()
      === moment(dates.max).format('YYYY-MM-DD').toString())
      ? moment.utc(dates.min).subtract(1, 'd')
      : moment.utc(dates.min)) : undefined
);

export const selectPerformanceDatesMax = createSelector(selectPerformanceDates, (dates?: DatesInterval) =>
  !!dates && dates.max !== undefined ? moment.utc(dates.max) : undefined
);

export const selectPerformanceYears = createSelector(
  selectPerformanceDatesMin,
  selectPerformanceDatesMax,
  (min?: moment.Moment, max?: moment.Moment) => getYearsDropdownValues(min, max)
);

export const selectPerformanceMonths = createSelector(
  selectPerformanceYearFilter,
  selectPerformanceDatesMin,
  selectPerformanceDatesMax,
  (year?: number, min?: moment.Moment, max?: moment.Moment) =>
    getMonthsDropdownValues(year, min, max)

);
